import { CustomerRecordTools } from "./CustomerRecordTools.tsx";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { getChangeVersion, subscribeChanges, notifyChanges } from "../changes.ts";
import { ExecutionHistory } from "../components/ExecutionHistory.tsx";
import { api, type ActionCard, type CustomerDetail as Detail, type FlywheelReport, type HandoffDraft, type IngestJob, type LossAnalysis, type TeamUser } from "../api.ts";
import { ActionCardView } from "../components/ActionCardView.tsx";
import { OnsiteRecorder } from "../components/OnsiteRecorder.tsx";
import { CustomerContacts } from "../components/CustomerContacts.tsx";
import { CustomerActionProgress } from "../components/CustomerActionProgress.tsx";
import { CustomerEventHistory } from "../components/CustomerEventHistory.tsx";
import { CustomerStageEditor } from "../components/CustomerStageEditor.tsx";
import { ScheduleEditor } from "../components/ScheduleEditor.tsx";
import { Icon } from "../components/Workspace.tsx";
import { setCurrentCustomer } from "../currentCustomer.ts";
import { goKnowledge } from "../navigate.ts";
import { ARTIFACT_SOURCE_TYPE_LABEL, CUSTOMER_SOURCE_LABEL, CUSTOMER_STAGES, CUSTOMER_STAGE_LABEL, CHANNEL_LABEL, fmtDate, fmtDateTime, fmtMoney, stageIndex, type CustomerStage } from "../labels.ts";
import "./sales-customers.css";

export function CustomerDetail(props: { customerId: string; onChanged?: () => void; openConsensusFor?: string; salesMode?: boolean; onClaim?: () => Promise<void> }): React.JSX.Element {
  const [d, setD] = useState<Detail>();
  const [card, setCard] = useState<ActionCard>();
  const [err, setErr] = useState("");
  const [modal, setModal] = useState<"none" | "won" | "lost">("none");
  const [flywheels, setFlywheels] = useState<FlywheelReport>();
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [editingStage, setEditingStage] = useState<string>();
  const [recordRequest, setRecordRequest] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [saved, setSaved] = useState("");
  const recordSection = useRef<HTMLDivElement>(null);
  const request = useRef(0);
  const load = useCallback(() => {
    const current = ++request.current;
    void Promise.all([api.get<Detail>(`/api/customers/${props.customerId}`), api.get<{ cards: ActionCard[] }>(`/api/action-cards?status=pending&scope=team&customerId=${props.customerId}`)])
      .then(([detail, cards]) => { if (request.current === current) { setD(detail); setCard(cards.cards[0]); setErr(""); } })
      .catch((e) => { if (request.current === current) setErr((e as Error).message); });
  }, [props.customerId]);
  const version = useSyncExternalStore(subscribeChanges, getChangeVersion, () => 0);
  useEffect(() => { load(); return () => { request.current++; }; }, [load, version]);
  useEffect(() => { const timer = setInterval(() => { if (document.visibilityState === "visible") load(); }, 8000); return () => clearInterval(timer); }, [load]);
  useEffect(() => { if (d?.customer) setCurrentCustomer({ id: d.customer.id, name: d.customer.name }); return () => setCurrentCustomer(undefined); }, [d?.customer.id, d?.customer.name]);
  const refresh = (): void => { notifyChanges(); props.onChanged?.(); };
  const record = (): void => { setRecordRequest((value) => value + 1); recordSection.current?.scrollIntoView({ block: "start", behavior: "smooth" }); };
  // AI 没到门槛的阶段判断:确认是采纳(不锁阶段),忽略只清掉
  const suggestionAction = (action: "confirm" | "dismiss"): void => {
    setSuggesting(true); setErr("");
    void api.post(`/api/customers/${props.customerId}/stage-suggestion/${action}`, {})
      .then(() => { setSaved(action === "confirm" ? "客户阶段已按 AI 的判断更新。" : "已忽略这条阶段建议，下次沟通后会重新判断。"); refresh(); })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setSuggesting(false));
  };
  if (err && !d) return <div className="err" role="alert">{err}<button className="b" onClick={load}>重新加载</button></div>;
  if (!d) return <p className="muted" role="status">正在加载客户…</p>;
  const c = d.customer;
  const settled = c.outcome !== "open";
  const canWork = c.canEdit && !settled && (Boolean(c.ownerId) || !props.salesMode);
  const canRead = Boolean(c.ownerId) || !props.salesMode;
  const cur = stageIndex(c.stage as CustomerStage);
  const recentEvents = new Set(d.events.slice(0, 3).map((event) => event.id));
  const needs = d.signals.filter((signal) => recentEvents.has(signal.eventId) && signal.kind === "need").slice(0, 2);
  const objections = d.signals.filter((signal) => recentEvents.has(signal.eventId) && signal.kind === "objection").slice(0, 2);
  return <div className="sales-customer-detail">
    {err && <p className="err" role="alert">{err}</p>}
    <header className="customer-detail-top"><div><h1>{c.name}</h1><div className="customer-detail-badges">{c.grade && <span className={`tag ${c.grade === "A" ? "tA" : "tN"}`}>{c.grade} · {c.currentScore ?? "—"}</span>}<span className={`tag ${c.outcome === "won" ? "tG" : c.outcome === "lost" ? "tR" : "tN"}`}>{c.outcome === "won" ? "已成交" : c.outcome === "lost" ? "已丢单" : CUSTOMER_STAGE_LABEL[c.stage as CustomerStage]}</span><span>{CUSTOMER_SOURCE_LABEL[c.source as keyof typeof CUSTOMER_SOURCE_LABEL] ?? c.source}</span><span>{c.ownerName ? `负责人：${c.ownerName}${c.ownerIsYou ? "（你）" : ""}` : "待认领"}</span></div></div>
      {canWork && <div className="customer-detail-actions"><button className="b p" onClick={record}><Icon name="audio" size={16} />记录沟通</button>{d.followup && <button className="b" onClick={() => setEditingSchedule(true)}><Icon name="calendar" size={16} />安排跟进</button>}<details className="customer-more-actions"><summary>更多操作</summary><div><button onClick={() => setModal("won")}>标记成交</button><button onClick={() => setModal("lost")}>标记丢单</button></div></details></div>}
    </header>
    {!c.ownerId && props.salesMode && <div className="customer-claim-notice"><div><b>这位客户还未认领</b><p>认领后可以记录沟通、安排跟进并处理行动建议。</p></div>{props.onClaim && <button className="b p" disabled={claiming} onClick={() => { setClaiming(true); setErr(""); void props.onClaim!().then(() => { setSaved("已认领客户，可以开始跟进。"); refresh(); }).catch((e) => setErr((e as Error).message)).finally(() => setClaiming(false)); }}>{claiming ? "认领中…" : "认领客户"}</button>}</div>}
    {saved && <p className="customer-detail-saved" role="status">{saved}<button className="icon-button" aria-label="关闭保存提示" onClick={() => setSaved("")}><Icon name="close" size={14} /></button></p>}
    <section className="card customer-overview" aria-label="客户跟进概况"><div className="customer-overview-facts"><div><span>最近沟通</span><b>{c.lastInteractionAt ? fmtDateTime(c.lastInteractionAt) : "尚未首触"}</b></div><div className={d.followup?.overdueDays ? "sales-followup-overdue" : ""}><span>下次跟进</span><b>{d.followup ? fmtDate(d.followup.dueOn) : settled ? "本单已结算" : "认领后安排"}</b>{d.followup && <small>{d.followup.overdueDays > 0 ? `逾期 ${d.followup.overdueDays} 天 · ` : ""}{CHANNEL_LABEL[d.followup.channel] ?? "联系客户"}{d.followup.manuallyArranged ? " · 已手动安排" : " · 按阶段建议"}</small>}</div><div><span>当前需求</span><p>{needs.length ? needs.map((signal) => signal.valueRaw).join("；") : "待沟通确认"}</p></div><div><span>当前关注</span><p>{objections.length ? objections.map((signal) => signal.valueRaw).join("；") : "暂无已记录的异议"}</p></div></div>
      {d.followup?.note && <p className="customer-followup-note"><Icon name="calendar" size={14} />{d.followup.note}</p>}
      <div className="customer-stage-heading"><span>销售进度{c.stageManuallySet ? " · 人工确认" : ""}</span>{canWork && <button className="b sm q" onClick={() => setEditingStage(c.stage)}>修正阶段</button>}{settled && <span>{c.outcome === "won" ? `签约金额 ${fmtMoney(c.wonAmount)}` : `丢单原因：${c.lostPrimaryReason ?? "待复盘"}`}</span>}</div>
      <ol className="customer-stage-progress" aria-label="客户销售阶段">{CUSTOMER_STAGES.map((stage, index) => <li key={stage} className={index === cur ? "current" : index < cur ? "past" : ""} aria-current={index === cur ? "step" : undefined}><span>{index + 1}</span>{CUSTOMER_STAGE_LABEL[stage]}</li>)}</ol>
      {c.stageSuggestion && canWork && c.stageSuggestion.stage !== c.stage && <div className="customer-stage-suggestion" role="status"><div><b>AI 判断可能已到「{CUSTOMER_STAGE_LABEL[c.stageSuggestion.stage as CustomerStage]}」</b><small>置信度 {Math.round(c.stageSuggestion.confidence * 100)}%，没到自动调整的门槛，等你确认</small>{c.stageSuggestion.reason && <p>{c.stageSuggestion.reason}</p>}</div><div className="customer-stage-suggestion-actions"><button className="b sm p" disabled={suggesting} onClick={() => suggestionAction("confirm")}>确认到{CUSTOMER_STAGE_LABEL[c.stageSuggestion.stage as CustomerStage]}</button><button className="b sm q" disabled={suggesting} onClick={() => suggestionAction("dismiss")}>忽略</button></div></div>}
      {d.transitions[0] && <details className="customer-stage-evidence"><summary>最近阶段依据</summary><p>{d.transitions[0].reason || "暂无详细说明"}</p><small>{fmtDateTime(d.transitions[0].createdAt)}</small></details>}
    </section>
    <div className="customer-work-layout"><div className="customer-work-main">
      <section className="card customer-next-step" aria-label="当前跟进建议"><div className="customer-section-heading"><h2>{settled ? "本单结果" : "建议下一步"}</h2></div>{settled ? <p className="hint">{c.outcome === "won" ? "已成交，可以查看沟通记录与复盘资料。" : `已丢单${c.lostSecondaryReason ? `，补充原因：${c.lostSecondaryReason}` : "，可以查看沟通记录与复盘资料。"}`}</p> : !canWork ? <p className="muted">认领客户后，可以处理跟进建议。</p> : card ? <ActionCardView key={card.id} card={card} onResolved={refresh} hideCustomer autoOpenConsensus={props.openConsensusFor === card.id} /> : <NextStepEmpty pendingJobId={d.pendingJobId} attempt={c.lastCardAttempt} onRecord={record} />}</section>
      {canRead && <div className="card customer-action-panel"><CustomerActionProgress customerId={props.customerId} version={version} canEdit={canWork} onChanged={refresh} onRecord={record} /></div>}
      <section className="card customer-communications" ref={recordSection} aria-label="客户沟通记录">{canRead ? <Events customerId={props.customerId} detail={d} onChanged={refresh} canEdit={canWork} requestOpen={recordRequest} /> : <p className="muted">认领后可查看完整沟通记录并继续跟进。</p>}</section>
      {canRead && <details className="card customer-execution-records"><summary>操作记录与撤销</summary><ExecutionHistory customerId={props.customerId} version={version + d.events.length} onChanged={refresh} /></details>}
    </div><aside className="customer-work-aside" aria-label="客户资料与联系人"><div className="card customer-contact-panel"><CustomerContacts customerId={props.customerId} contacts={d.contacts} canEdit={canWork} onChanged={refresh} /></div><section className="card customer-background"><div className="customer-section-heading"><h3>客户资料</h3></div><p>{[c.industry, c.sizeBand, c.region].filter(Boolean).join(" · ") || "行业与地区待补充"}</p><details><summary>资料详情与匹配依据</summary><dl><dt>法定代表人</dt><dd>{c.legalName || "—"}</dd><dt>主营产品</dt><dd>{c.products || "—"}</dd><dt>注册资本</dt><dd>{c.registeredCapital || "—"}</dd><dt>地址</dt><dd>{c.address || "—"}</dd><dt>资料情况</dt><dd>{c.dataQuality || "待核对"}</dd></dl><CustomerRecordTools key={c.id} customer={props.salesMode && !c.ownerId ? { ...c, canEdit: false } : c} onChanged={refresh} /></details></section>{canWork && <details className="card customer-handoff"><summary>客户交接</summary><Handoff customerId={props.customerId} disabled={settled} onChanged={refresh} /></details>}</aside></div>
    {editingSchedule && d.followup && <ScheduleEditor item={d.followup} onClose={() => setEditingSchedule(false)} onSaved={(followup) => { setEditingSchedule(false); setD({ ...d, followup: { ...d.followup!, ...followup, manuallyArranged: true } }); setSaved("跟进安排已保存。"); refresh(); }} />}
    {editingStage && <CustomerStageEditor customerId={c.id} customerName={c.name} stage={editingStage} onClose={() => setEditingStage(undefined)} onSaved={() => { setEditingStage(undefined); setSaved("客户阶段已更新。"); refresh(); }} />}
    {modal !== "none" && <SettleModal customerId={props.customerId} customerName={c.name} outcome={modal} onClose={() => setModal("none")} onSettled={(result) => { setFlywheels(result); setModal("none"); setSaved("本单结果已保存，复盘资料将同步更新。"); refresh(); }} />}
    {flywheels && <details className="card customer-settlement-result"><summary>查看复盘更新</summary><p>{flywheels.profile.detail}</p><p>{flywheels.knowledge.detail}</p><p>{flywheels.autonomy.detail}</p></details>}
  </div>;
}

/** 事件流 + 投递入口。投的是生料,不是填字段。 */
function Events(props: {
  customerId: string; detail: Detail; onChanged: () => void; canEdit: boolean; requestOpen: number;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  useEffect(() => { if (props.requestOpen) setOpen(true); }, [props.requestOpen]);
  const [sourceType, setSourceType] = useState("chat_log");
  const [job, setJob] = useState<IngestJob | undefined>(undefined);
  const [err, setErr] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const changed = useRef(props.onChanged);
  useEffect(() => { changed.current = props.onChanged; }, [props.onChanged]);
  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  /** 告知文案取租户配置 —— 管理者改了它,销售这里念的就得跟着变。 */
  const [notice, setNotice] = useState("");
  useEffect(() => {
    void api.get<{ notice: string }>("/api/settings/recording-notice")
      .then((r) => setNotice(r.notice)).catch(() => {});
  }, []);

  /** 录音类必须勾「已告知对方」——个保法要求,后端也拦,这里先说清楚。 */
  const RECORDING = new Set(["call_recording", "onsite_recording", "meeting_record"]);
  const needsConsent = RECORDING.has(sourceType);

  /** 轮询直到跑完或失败。投递秒回是硬验收项,所以进度在前端轮,不在请求里等。 */
  const poll = useCallback((jobId: string) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    const timer = setInterval(() => {
      void api.get<{ job: IngestJob }>(`/api/ingest/jobs/${jobId}`).then((r) => {
        setJob(r.job);
        if (r.job.finishedAt || r.job.failed || r.job.awaitingConfirmation) {
          clearInterval(timer);
          if (r.job.finishedAt) changed.current();
        }
      }).catch(() => clearInterval(timer));
    }, 2500);
    pollTimer.current = timer;
    return timer;
  }, []);
  useEffect(() => {
    if (props.detail.pendingJobId && props.detail.pendingJobId !== job?.id) poll(props.detail.pendingJobId);
  }, [props.detail.pendingJobId, job?.id, poll]);

  const submit = async (): Promise<void> => {
    if (!text.trim() || uploading) return;
    setErr(""); setUploading(true);
    try {
      const r = await api.post<{ jobId: string }>("/api/ingest/text", {
        customerId: props.customerId, sourceType, text: text.trim(),
      });
      setText(""); setOpen(false);
      poll(r.jobId);
    } catch (e) { setErr((e as Error).message); } finally { setUploading(false); }
  };

  /**
   * 文件投递三步:要签名 → **浏览器直传**(配了 OSS 时文件根本不经过我们的服务器)→ 登记入队。
   * 一段 30 分钟录音几十兆,走服务器中转既占内存又占带宽。
   */
  const submitFile = async (inputFile: File | undefined = file): Promise<void> => {
    if (!inputFile) return;
    if (needsConsent && !consent) { setErr("请先确认已向对方告知录音用途。"); return; }
    setErr(""); setUploading(true);
    try {
      const signed = await api.post<{
        artifactId: string;
        upload: { url: string; method: string; headers: Record<string, string> };
      }>("/api/ingest/sign", {
        customerId: props.customerId, sourceType,
        filename: inputFile.name, mimeType: inputFile.type || "application/octet-stream",
        byteSize: inputFile.size, consentAcknowledged: consent,
      });

      const put = await fetch(signed.upload.url, {
        method: signed.upload.method,
        headers: signed.upload.headers,
        body: inputFile,
        // 应用代理要带会话;OSS 预签名请求不携带浏览器凭证。
        credentials: signed.upload.url.startsWith("/") ? "include" : "omit",
      });
      if (!put.ok) throw new Error(`上传失败(${put.status})`);

      const r = await api.post<{ jobId: string }>("/api/ingest/submit", {
        artifactId: signed.artifactId, customerId: props.customerId, sourceType,
        filename: inputFile.name, mimeType: inputFile.type || "application/octet-stream",
        byteSize: inputFile.size, consentAcknowledged: consent,
      });
      setFile(undefined); setConsent(false); setOpen(false);
      poll(r.jobId);
    } catch (e) { setErr((e as Error).message); } finally { setUploading(false); }
  };

  const retry = async (): Promise<void> => {
    if (!job) return;
    try {
      await api.post(`/api/ingest/jobs/${job.id}/retry`);
      poll(job.id);
    } catch (e) { setErr((e as Error).message); }
  };


  return (
    <>
      <div className="secttl">
        <span>沟通记录</span>
        {props.canEdit && <button className="b sm p" onClick={() => setOpen(!open)}>记录沟通 / 上传资料</button>}
      </div>

      {open && props.canEdit && (
        <div className="chatbox">
          <div className="ttl">记录本次沟通</div>
          <div className="row" style={{ marginBottom: 8 }}>
            {Object.entries(ARTIFACT_SOURCE_TYPE_LABEL).map(([k, label]) => (
              <button key={k} className={`b sm ${sourceType === k ? "p" : ""}`} onClick={() => setSourceType(k)}>
                {label}
              </button>
            ))}
          </div>
          <textarea className="in" style={{ width: "100%", minHeight: 96 }}
            placeholder="粘贴聊天记录 / 通话纪要原文。AI 会做结构化,你不用整理。"
            value={text} onChange={(e) => setText(e.target.value)} />
          <div className="btns" style={{ marginTop: 8 }}>
            <button className="b p" onClick={() => { void submit(); }} disabled={!text.trim() || uploading}>
              保存沟通
            </button>
            <button className="b q" onClick={() => setOpen(false)}>取消</button>
          </div>

          <div className="secttl" style={{ marginTop: 10 }}><span>或者上传录音、录屏、聊天截图或文档</span></div>
          <div className="form-row row">
            <input type="file" accept="audio/*,video/*,.mp3,.m4a,.wav,.mp4,.webm,.pdf,.docx,.pptx,.xlsx,.txt,.md,.png,.jpg,.jpeg,.webp"
              onChange={(e) => {
                const chosen = e.target.files?.[0]; setFile(chosen); setErr("");
                if (!chosen) return;
                const video = chosen.type.startsWith("video/") || /\.(mp4|mov|mkv)$/i.test(chosen.name);
                const audio = chosen.type.startsWith("audio/") || /\.(mp3|m4a|wav|aac|aiff|ogg|webm)$/i.test(chosen.name);
                if (video) setSourceType("meeting_record");
                else if (audio && !RECORDING.has(sourceType)) setSourceType("call_recording");
                else if (!audio && !video && RECORDING.has(sourceType)) setSourceType("text_photo");
              }} />
            <button className="b p" disabled={!file || uploading} onClick={() => { void submitFile(); }}>
              {uploading ? "上传中…" : "上传资料"}
            </button>
          </div>
          {file && (
            <div className="muted" style={{ fontSize: "var(--t-xs)" }}>
              {file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB
            </div>
          )}
          {needsConsent && (
            <label className="hint warn" style={{ display: "block", marginTop: 6 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              {" "}我已向对方说明以下录音告知内容
              {/* 文案取租户配置,与管理者在「录入引擎 → 录音告知与授权」里编辑的是同一份 */}
              <div style={{ marginTop: 4, paddingLeft: 20, fontStyle: "italic" }}>
                {notice ? `「${notice}」` : "正在读取录音告知内容"}
              </div>
            </label>
          )}
          {sourceType === "onsite_recording" && <OnsiteRecorder consent={consent} notice={notice} disabled={uploading}
            onRecorded={(recorded) => { setFile(recorded); void submitFile(recorded); }} />}
          <div className="hint">
            上传后会在后台整理，处理期间可以继续其他工作。
          </div>
        </div>
      )}

      {err && <div className="err">{err}</div>}

      {job && (
        <div className="chatbox">
          <div className="row">
            {job.steps.map((s) => (
              <span key={s.key} className={`tag ${s.failed ? "tR" : s.done ? "tG" : s.current ? "tA" : "tN"}`}>
                {s.done ? "✓ " : s.failed ? "✕ " : ""}{s.label}
              </span>
            ))}
          </div>
          {job.failed && (
            <div className="hint err" style={{ marginTop: 8 }}>
              <b>{job.failed.label}</b>(断在「{job.failed.step}」,第 {job.attempts} 次)
              <div>{job.failed.advice}</div>
              {job.failed.reason && <div className="muted" style={{ fontSize: "var(--t-xs)" }}>技术原因:{job.failed.reason}</div>}
              {job.failed.retryable && (
                <button className="b sm" style={{ marginTop: 6 }} onClick={() => { void retry(); }}>重试</button>
              )}
            </div>
          )}
          {job.awaitingConfirmation && <div className="hint" role="status">
            <p>{job.awaitingConfirmation}</p>
            <button className="b p" onClick={() => { void api.post(`/api/ingest/jobs/${job.id}/confirm`, {})
              .then(() => poll(job.id)).catch((e) => setErr((e as Error).message)); }}>确认处理这份材料</button>
          </div>}
          {job.cancelled ? <div className="hint">已撤销派生结果,原始材料仍保留。</div>
            : job.finishedAt && <div className="hint ok" style={{ marginTop: 8 }}>✓ 已完成结构化、阶段分析与行动判断</div>}
        </div>
      )}

      <CustomerEventHistory customerId={props.customerId} version={`${JSON.stringify(props.detail.events)}:${props.detail.signals.length}`} />
    </>
  );
}

/** 交接 + AI 摘要。封顶 L2 —— 接手人的手机已经响了,对人不可逆。 */
function Handoff(props: { customerId: string; disabled: boolean; onChanged: () => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [to, setTo] = useState("");
  const [draft, setDraft] = useState<HandoffDraft | undefined>(undefined);
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || users.length) return;
    void api.get<{ users: TeamUser[] }>("/api/users")
      .then((r) => { setUsers(r.users); setTo(r.users[0]?.id ?? ""); })
      .catch((e) => setErr(String((e as Error).message)));
  }, [open, users.length]);

  const gen = async (): Promise<void> => {
    setBusy(true); setErr("");
    try { setDraft(await api.post<HandoffDraft>(`/api/customers/${props.customerId}/handoff/summary`)); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const confirm = async (): Promise<void> => {
    if (!draft || !to) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/api/customers/${props.customerId}/handoff`, {
        toUserId: to, summaryMarkdown: draft.markdown, confirmed: true,
      });
      setDone(users.find((u) => u.id === to)?.name ?? "接手人");
      setDraft(undefined);
      props.onChanged();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  if (props.disabled) return <></>;

  return (
    <>
      <div className="secttl">
        <span>交接</span>
        <button className="b sm" onClick={() => setOpen(!open)}>🔄 交接给同事</button>
      </div>
      {done && <div className="hint ok">✓ 已交接给 {done},摘要与事件流已一并转移</div>}
      {open && !done && (
        <div className="chatbox">
          <div className="ttl">🔄 客户交接 <span>— AI 自动生成交接摘要,接手的人不用从头听录音</span></div>
          {err && <div className="hint err">{err}</div>}
          <div className="form-row row">
            <span style={{ fontSize: 12 }}>交接给:</span>
            <select className="in" value={to} onChange={(e) => setTo(e.target.value)}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {!draft && <button className="b p" disabled={busy || !to} onClick={() => { void gen(); }}>
              {busy ? "生成中…" : "生成交接摘要"}
            </button>}
            <button className="b q" onClick={() => { setOpen(false); setDraft(undefined); }}>取消</button>
          </div>
          {draft && (
            <>
              <div className="hint" style={{ background: "var(--card)", border: "1px solid var(--line)", color: "var(--ink)" }}>
                <b>🤖 AI 交接摘要 · {draft.customerName}</b><br />
                • <b>进展</b>:{draft.summary.progress}<br />
                • <b>关键人</b>:{draft.summary.keyPeople.join(" / ") || "(尚未识别)"}<br />
                • <b>当前卡点</b>:{draft.summary.blockers.join(" / ") || "(无)"}<br />
                • <b>下一步</b>:{draft.summary.nextStep}<br />
                • <b>附</b>:{draft.attachments.events} 条事件流、{draft.attachments.documents} 份客户专属资料、
                {draft.attachments.contacts} 位决策链联系人一并转移
              </div>
              <div className="btns" style={{ marginTop: 8 }}>
                <button className="b p" disabled={busy} onClick={() => { void confirm(); }}>
                  确认交接
                </button>
                <button className="b q" onClick={() => setDraft(undefined)}>重新生成</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** 成交 / 丢单结算弹窗。丢单原因由 AI 从事件流提取,不给下拉框。 */
function SettleModal(props: {
  customerId: string; customerName: string; outcome: "won" | "lost";
  onClose: () => void; onSettled: (f: FlywheelReport) => void;
}): React.JSX.Element {
  const won = props.outcome === "won";
  const [amount, setAmount] = useState("");
  const [cycle, setCycle] = useState("");
  const [loss, setLoss] = useState<LossAnalysis | undefined>(undefined);
  const [note, setNote] = useState("");
  /** AI 归因停用(L0)时人自己写的主因。留空就记「未归因」,不替人编。 */
  const [manualPrimary, setManualPrimary] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (won) return;
    setBusy(true);
    void api.post<{ loss: LossAnalysis }>(`/api/customers/${props.customerId}/settlement/preview`, { outcome: "lost" })
      .then((r) => setLoss(r.loss))
      .catch((e) => setErr(String((e as Error).message)))
      .finally(() => setBusy(false));
  }, [won, props.customerId]);

  /** 销售补一句 → AI 重新归因。这是纠正,不是代填。 */
  const reAttribute = async (): Promise<void> => {
    setBusy(true); setErr("");
    try {
      const r = await api.post<{ loss: LossAnalysis }>(
        `/api/customers/${props.customerId}/settlement/preview`, { outcome: "lost", note },
      );
      setLoss(r.loss);
      setNote("");
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const confirm = async (): Promise<void> => {
    setBusy(true); setErr("");
    try {
      const r = await api.post<{ flywheels: FlywheelReport }>(`/api/customers/${props.customerId}/settlement`, {
        outcome: props.outcome,
        confirmed: true,
        ...(won ? { amount: Number(amount) } : {}),
        ...(won && cycle ? { cycleDays: Number(cycle) } : {}),
        // 把弹窗里显示的那一份原样传回去 —— 人确认的就是屏幕上这一份,
        // 只回传主因会把 AI 提取到的竞品丢掉,复盘就归不进竞品库了
        ...(!won && loss && !loss.aiDisabled ? {
          lossPrimary: loss.primary,
          lossSecondary: loss.secondary || undefined,
          lossCompetitor: loss.competitor || undefined,
          lossEvidence: loss.evidence.length ? loss.evidence : undefined,
        } : {}),
        ...(!won && loss?.aiDisabled ? {
          ...(manualPrimary.trim() ? { lossPrimary: manualPrimary.trim() } : {}),
          ...(note.trim() ? { lossNote: note.trim() } : {}),
        } : {}),
      });
      props.onSettled(r.flywheels);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={props.onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mt">{won ? "🎉 标记成交" : "标记丢单"}</div>
        <div className="ms">
          {props.customerName} · {won ? "请核对签约金额与成交信息" : "核对丢单原因与沟通依据后保存"}
        </div>

        {err && <div className="err">{err}</div>}

        {won ? (
          <div className="box">
            <b>签约信息:</b>
            <div className="form-row row" style={{ marginTop: 6 }}>
              <span>金额</span>
              <input className="in" style={{ width: 120 }} placeholder="380000" value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} /> 元
              <span style={{ marginLeft: 10 }}>周期</span>
              <input className="in" style={{ width: 90 }} placeholder="不填就按首次沟通算" value={cycle}
                onChange={(e) => setCycle(e.target.value.replace(/[^\d]/g, ""))} /> 天
            </div>
          </div>
        ) : (
          <div className="box lose">
            <b className="h">{loss?.aiDisabled ? "丢单原因:" : "🤖 AI 从沟通记录里分析的丢单原因:"}</b>
            {busy && !loss && <div className="muted">分析中…</div>}
            {loss?.aiDisabled && (
              <>
                <div className="muted">你的「丢单原因提取」AI 权限是只观察,这次没有让 AI 读沟通记录。请自己写明主因,留空会记为「未归因」。</div>
                <div className="form-row row" style={{ marginTop: 8 }}>
                  <input className="in" style={{ width: 200 }} maxLength={20} aria-label="丢单主因" placeholder="主因,如:预算取消"
                    value={manualPrimary} onChange={(e) => setManualPrimary(e.target.value)} />
                  <input className="in" style={{ flex: 1 }} maxLength={500} aria-label="丢单补充说明" placeholder="补充说明(可选)"
                    value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              </>
            )}
            {loss && !loss.aiDisabled && (
              <>
                <div>主因:<b>{loss.primary}</b>{loss.secondary ? ` · 次因:${loss.secondary}` : ""}
                  {loss.competitor ? ` · 竞品:${loss.competitor}` : ""}</div>
                {loss.note && <div className="muted">{loss.note}</div>}
                {loss.evidence.length > 0 && (
                  <div className="muted" style={{ fontSize: "var(--t-xs)", marginTop: 4 }}>
                    依据:{loss.evidence.map((e) => `「${e}」`).join(" ")}
                  </div>
                )}
                <div className="form-row row" style={{ marginTop: 8 }}>
                  <input className="in" style={{ flex: 1 }} placeholder="觉得不对?补一句,AI 重新归因"
                    value={note} onChange={(e) => setNote(e.target.value)} />
                  <button className="b sm" disabled={busy || !note.trim()} onClick={() => { void reAttribute(); }}>
                    重新归因
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className={`box ${won ? "fly" : ""}`}>
          <b className="h">确认后会立刻发生:</b><br />
          {won ? (
            <>① 更新客户匹配依据，供后续客户筛选参考<br />
              ② 整理赢单复盘，保存到团队案例与话术<br />
              ③ 将成交结果关联到本次跟进建议</>
          ) : (
            <>① 保存丢单原因与相关经验<br />
              ② 补充后续客户筛选与匹配的参考依据<br />
              ③ 将丢单结果关联到本次跟进建议<br />
              <span className="muted">被拒绝的理由,就是下次改进的方向</span></>
          )}
        </div>

        <div className="foot">
          <button className="b q" onClick={props.onClose}>取消</button>
          <button className="b p" disabled={busy || (won && !amount)} onClick={() => { void confirm(); }}>
            {busy ? "处理中…" : won ? "确认成交" : "确认丢单并归档"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 「建议下一步」空着时说清为什么(#63):查不到依据不出卡是设计,但页面不能空着让人以为系统没在看。
 * 系统记的是最近一次出卡尝试的结果;出过卡又处理完了,就回到默认文案。
 */
function NextStepEmpty(props: { pendingJobId?: string | null; attempt: Detail["customer"]["lastCardAttempt"]; onRecord: () => void }): React.JSX.Element {
  const a = props.attempt;
  if (props.pendingJobId) return <div className="customer-next-empty"><p>沟通资料正在处理，完成后会更新进展与建议。</p></div>;
  if (a?.outcome === "no_knowledge") return <div className="customer-next-empty"><p>最近一次沟通后没有生成建议：知识库里查不到能作依据的资料，系统不出没有出处的建议。这个缺口已经记给管理员，上传相关资料（审核通过后）下次沟通就会出建议。</p><button className="b sm" onClick={() => goKnowledge()}>去知识库上传资料</button></div>;
  if (a?.outcome === "no_citation") return <div className="customer-next-empty"><p>最近一次沟通后没有生成建议：模型没有找到可引用的依据，系统不出没有出处的建议。记录下一次沟通后会重试。</p><button className="b sm" onClick={props.onRecord}>记录沟通</button></div>;
  if (a?.outcome === "autonomy_l0") return <div className="customer-next-empty"><p>当前 AI 自主度为「只观察」，系统不主动给建议。可以在自主度设置里调整。</p><button className="b sm" onClick={props.onRecord}>记录沟通</button></div>;
  return <div className="customer-next-empty"><p>暂无待处理建议，可先记录沟通或安排下次跟进。</p><button className="b sm" onClick={props.onRecord}>记录沟通</button></div>;
}
